import React from 'react';
import { ReportsStaffAllocation, StaffWarning } from '@go-shared/types/staffAllocation';
import {
  timeToMinutes,
  timeToString,
  minutesToTime,
  minutesToTimeString,
  addMinutesToTime,
  DEFAULT_TIMESTR_FORMAT,
  DEFAULT_HOUR_FORMAT,
} from '@go-shared/lib/time';
import { Time } from '@go-shared/types/time';
import { HourFormat } from '@go-shared/types/brand';

export interface StaffGroupProps {
  staffGroup: ReportsStaffAllocation[];
  shipStartTime: Time;
  hourFormat: HourFormat;
  staffWarnings: StaffWarning[];
  index: number;
}

export const StaffGroup: React.FC<StaffGroupProps> = ({
  staffGroup,
  hourFormat,
  staffWarnings,
  index,
}) => {
  let totalTime = 0;
  staffGroup.forEach(staff => {
    totalTime += staff.duration;
  });

  const checkConflict = (staffId: string) =>
    staffWarnings.find(el => el.template._id === staffId) ? 'bg-yellow-lighter' : '';

  const getEventsSortedByStartTime = () => {
    return staffGroup.sort((s1, s2) => {
      const s1Start = timeToMinutes(s1.eventStartTime);
      const s2Start = timeToMinutes(s2.eventStartTime);
      if (s1Start < s2Start) return -1;
      else return 1;
    });
  };

  return (
    <>
      {getEventsSortedByStartTime().map((s, i) => {
        return (
          <React.Fragment key={i}>
            <div
              className={`${index % 2 !== 0 ? 'bg-grey-lighter' : ''} ${checkConflict(
                s.template._id
              )}`}
            >
              <div className={'reports-row w-full inline-flex justify-between pl-5 pr-5'} key={i}>
                {/* Department */}
                <div className="w-1/6 flex items-center pr-2">
                  {i === 0 && <p>{s.template.department.name}</p>}
                </div>
                {/* Staff Title */}
                <div className="w-1/6 flex items-center pr-2">
                  {i === 0 && <p>{s.template.title}</p>}
                </div>
                {/* Event */}
                <div className="w-1/6 flex items-center pr-2 overflow-hidden">
                  <p className="overflow-ellipsis">{s.eventName}</p>
                </div>
                {/* Venue */}
                <div className="w-1/6 flex items-center pr-2">
                  <p>{s.venueName}</p>
                </div>
                {/* Event Time */}
                <div className="w-1/6 flex items-center pr-2">
                  <p>{getEventTimes(s)}</p>
                </div>
                {/* Involvement */}
                <div className="w-1/6 flex items-center pr-2">
                  <p>{getStaffInvolvements(s, hourFormat)}</p>
                </div>
                {/* Function */}
                <div className="w-1/6 flex items-center pr-2">
                  <p>{s.activity}</p>
                </div>
                {/* Length */}
                <div className="col-length flex items-center pr-2">
                  <p>{minutesToTimeString(s.duration, '24', DEFAULT_TIMESTR_FORMAT)}h</p>
                </div>
                {/* Total hours */}
                <div className="col-total flex items-center pr-2">
                  {i === 0 && (
                    <p>{minutesToTimeString(totalTime, '24', DEFAULT_TIMESTR_FORMAT)}h</p>
                  )}
                </div>
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </>
  );
};

const getEventTimes = (staffGroup: ReportsStaffAllocation) => {
  const eventStart = staffGroup.eventStartTime;
  const end = staffGroup.eventDuration;
  const startInMinutes = timeToMinutes(eventStart);
  const eventEnd = minutesToTime(end + startInMinutes);
  return (
    timeToString(eventStart, DEFAULT_HOUR_FORMAT, DEFAULT_TIMESTR_FORMAT) +
    ' - ' +
    timeToString(eventEnd, DEFAULT_HOUR_FORMAT, DEFAULT_TIMESTR_FORMAT)
  );
};

const getStaffInvolvements = (staffGroup: ReportsStaffAllocation, hourFormat: HourFormat) => {
  const eventStart = staffGroup.eventStartTime;
  const staffStart = addMinutesToTime(eventStart, staffGroup.startTime);

  const staffEnd = addMinutesToTime(staffStart, staffGroup.duration);

  return (
    timeToString(staffStart, hourFormat, DEFAULT_TIMESTR_FORMAT) +
    ' - ' +
    timeToString(staffEnd, hourFormat, DEFAULT_TIMESTR_FORMAT)
  );
};
